import React, { useEffect, useMemo, useRef, useState } from 'react'

import { Box, Stack, Typography, useTheme } from '@mui/material'
import { Activity as ActivityIcon, SearchX } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import {
  ListRow,
  ListRowIcon,
  PageContainer,
  PageHeader,
  ToolbarSearchField,
  LIST_ROW_LEADING_SIZE,
} from 'components/UI'
import { PROSE_MAX_WIDTH } from 'constants/layout'
import { searchDebounceDelay } from 'settings'
import { FadeInImage } from 'components/UI/FadeInImage'
import { tileRadius } from 'settings/brandRadius'
import { CertificateStatusChip } from 'components/pages/certificates/CertificateStatusChip'
import {
  getActivityTypeConfig,
  getSelectedGroupKeys,
} from 'components/pages/certificates/productPage/activityTypes'
import { useActivityFeed, ActivityFeedItem } from 'hooks/api/useActivityFeed'
import { useScrolledPast } from 'hooks/useScrolledPast'
import { useSelector } from 'store/hooks'
import { formatActivityTime, formatedDate } from 'utils/date'
import { glassStyle, glassUnsupportedSx } from 'utils/glass'

import { FilterControl } from 'components/UI/ToolbarControl'
import {
  ActivityFilterDrawer,
  type ActivityFilterValues,
} from 'components/pages/certificates/productPage/ActivityFilterDrawer'

import { ActivityFeedSkeleton } from './ActivityFeedSkeleton'
import { useActivityLastSeen } from './useActivityLastSeen'

/** Rows pulled per infinite-scroll step. */
const FEED_PAGE_SIZE = 20

/**
 * The boundary between rows that have arrived since the reader's last visit
 * (above) and rows they have already looked at (below).
 *
 * ## Why a divider and not a per-row badge
 *
 * The feed is already newest-first, so "new" is a contiguous run at the top —
 * one property of the LIST, not of each row. A dot or an "N" chip on every
 * unseen row restates that same fact once per row, which on a first visit after
 * a busy week means fifteen identical markers saying one thing. A single line
 * says it once, in the one place it changes.
 *
 * ## Why it is this quiet
 *
 * `grey[200]`, the same hairline the rest of this app separates rows with, and
 * `text.secondary` at 12px. No accent colour and no chip, on purpose: this is
 * wayfinding, not an alert. Nothing above the line is a problem — it is just
 * material the reader has not scrolled past yet — and a coloured mark would
 * borrow the vocabulary the status chips use for things that ARE wrong.
 *
 * ## Why "Already seen" and not "Earlier"
 *
 * "Earlier" is true and says nothing: the list is newest-first, so everything
 * below any given point is earlier by construction, and a label that restates
 * the sort order carries no information. "Already seen" names the only property
 * the line actually marks — the reader's own history with these rows.
 */
const SeenDivider: React.FC = () => {
  const theme = useTheme()

  const rule = (
    <Box
      sx={{
        flexGrow: 1,
        height: '1px',
        backgroundColor: theme.palette.grey[200],
      }}
    />
  )

  return (
    <Stack
      direction='row'
      alignItems='center'
      /* 12px on this app's 8px MUI base — NOT @repo/ui's 4px base, where this
         same value would be 6px and the label would sit almost touching the
         rule (working conventions §29). */
      spacing={1.5}
      /* `aria-hidden`: a screen reader moving row by row has no use for a
         decorative rule announced as "Already seen" between two events, and the
         label is not a heading it can navigate to. The information it carries is
         positional, and position is what a visual scan uses. */
      aria-hidden
      sx={{
        /* 16px, matching a row's own `py`, so the band reads as one row's worth
           of pause rather than a gap someone forgot to close. */
        py: 2,
      }}
    >
      {rule}
      <Typography
        sx={{
          fontSize: '12px',
          color: 'text.secondary',
          /* Never wraps or shrinks: a two-word label that broke across lines
             would push the rules apart and stop reading as one line. */
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        Already seen
      </Typography>
      {rule}
    </Stack>
  )
}

/**
 * The certificate's photo in a row's leading slot.
 *
 * Falls back to the EVENT's glyph rather than a generic placeholder box: a row
 * with no product image still has something true to show, and the glyph is the
 * same one the per-certificate timeline uses for that event type, so the two
 * surfaces stay legible as one system.
 *
 * `FadeInImage`, not a bare `<img>` — a list of photos that each hard-cut in at
 * its own moment reads as the page assembling itself (see FadeInImage's own
 * note). The box is pre-sized, so nothing shifts either way.
 */
const CertificateThumb: React.FC<{ item: ActivityFeedItem }> = ({ item }) => {
  const theme = useTheme()
  const { Icon } = getActivityTypeConfig(item.type)

  if (!item.certificate.image) return <ListRowIcon icon={Icon} />

  return (
    <Box
      sx={{
        width: LIST_ROW_LEADING_SIZE,
        height: LIST_ROW_LEADING_SIZE,
        // VRG-562: follows the tenant's Corner radius. Clamped to the row's own leading size.
        borderRadius: tileRadius(LIST_ROW_LEADING_SIZE),
        overflow: 'hidden',
        flexShrink: 0,
        backgroundColor: theme.palette.grey[100],
      }}
    >
      <FadeInImage
        src={item.certificate.image}
        alt=''
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </Box>
  )
}

/**
 * Nothing to show — and WHY nothing, which is the whole point of the props.
 *
 * "No activity yet" is true only of an owner who has never had an event. Said
 * to someone who has just typed a product name, it asserts their whole history
 * is empty and gives them no reason to suspect the search box is what emptied
 * the screen. The two states look identical (an empty list) and mean opposite
 * things (nothing exists / nothing matched), so the copy is the only thing that
 * can distinguish them.
 *
 * Extended rather than replaced: the unfiltered case keeps exactly the wording
 * and glyph it shipped with, so the common state is untouched.
 */
const EmptyState: React.FC<{
  /** The search term the empty result was produced BY. Debounced, not the raw input. */
  query?: string
  /** How many event-type groups the Filter sheet currently has selected. */
  typeFilterCount?: number
}> = ({ query, typeFilterCount = 0 }) => {
  const theme = useTheme()

  const isNarrowed = Boolean(query) || typeFilterCount > 0
  /* The magnifier only when a SEARCH is what emptied it. A type filter is not a
     search, and pointing at the wrong control is worse than staying neutral. */
  const Glyph = query ? SearchX : ActivityIcon

  const title = isNarrowed ? 'No matching activity' : 'No activity yet'
  /*
    BOTH narrowings get named when both are on, and that case is checked FIRST.

    Ordering `query` ahead of `typeFilterCount` made search take the blame for every
    empty result: with the Transfers filter on and "Jacket" typed, this said 'Nothing
    matches "Jacket". Try another product name or SKU.' — while "Jacket" on its own
    returns four events and the TYPE filter is what emptied the screen. The reader then
    edits the search term, which cannot possibly help, and the one control that would
    is never mentioned.

    An empty state's only job is to name what to change. Naming the wrong control is
    worse than naming nothing.
  */
  const body =
    query && typeFilterCount > 0
      ? `No ${typeFilterCount === 1 ? 'event of that type' : 'events of those types'} match "${query}". Try a different term, or widen the filter.`
      : query
        ? `Nothing matches "${query}". Try another product name or SKU.`
        : typeFilterCount > 0
          ? 'No events of the types you have selected. Try widening the filter.'
          : 'Transfers, status changes and other events across your certificates will appear here.'

  return (
    <Stack
      sx={{
        textAlign: 'center',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 276,
        py: 4,
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          backgroundColor: theme.palette.grey[50],
          border: `1px solid ${theme.palette.grey[300]}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}
      >
        <Glyph size={24} color={theme.palette.text.primary} />
      </Box>
      <Typography sx={{ fontWeight: 500, color: 'text.primary', mb: 0.5, fontSize: '16px' }}>
        {title}
      </Typography>
      <Typography sx={{ color: 'text.secondary', maxWidth: 320, fontSize: '14px' }}>
        {body}
      </Typography>
    </Stack>
  )
}

/**
 * VERA-315 — the Activity tab: one reverse-chronological feed of everything that
 * has happened across ALL of the reader's certificates.
 *
 * ## What this replaced
 *
 * The tab used to render the notification-PREFERENCES page — contact rows and
 * email/SMS switches. That is a settings surface wearing a destination's name,
 * and it is now under Profile with the rest of the account settings. A tab
 * called Activity should answer "what happened", and until this existed nothing
 * in the app could: activity was per-certificate only, so noticing that a
 * transfer had completed meant opening each certificate in turn.
 *
 * ## Why one endpoint and not a fan-out
 *
 * `GET /certificates/customer/activity` was added for this. The tempting
 * shortcut — call the existing per-certificate endpoint once per certificate and
 * merge client-side — is wrong in a way that gets worse with use: it is N
 * requests for one screen, and it cannot paginate, because the correct first
 * page of a merged feed is not derivable from the first page of each source.
 *
 * ## Scans are excluded, on purpose
 *
 * Same exclusion the per-certificate timeline makes, for the same reason: raw
 * scan events are high-volume and would bury every transfer and status change
 * under tap noise. Scans stay on a certificate's own Scans list.
 */
export const ActivityPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()

  // Single growing window rather than accumulated pages — the same idiom the
  // per-certificate timeline and the Scans list use. The API returns the top N
  // by date for `offset: 0`, so re-requesting a larger N cannot drift out of
  // order the way client-side concatenation can.
  const [visibleCount, setVisibleCount] = useState<number>(FEED_PAGE_SIZE)

  /*
   * Filter state lives here and goes to the SERVER, so `total` and the pages
   * that follow are both computed against it. Client-side filtering over the
   * loaded window would make the count above the list a lie the moment the feed
   * outgrew one page — see the hook.
   */
  const [filterOpen, setFilterOpen] = useState(false)
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  /* Groups, not raw types, is what the sheet shows and what the reader counts —
     but the sheet hands back expanded raw types, so count the GROUPS that are
     fully represented rather than printing "Filter (11)" for one tick. */
  const activeTypeCount = useMemo(
    () => getSelectedGroupKeys(selectedTypes).length,
    [selectedTypes],
  )

  /*
   * The search term, SERVER-side like the type filter and for the same reason.
   *
   * This holds the DEBOUNCED value — `ToolbarSearchField` is uncontrolled and
   * only calls back once typing settles, so this state changes about once per
   * search rather than once per keystroke. That matters beyond request volume:
   * it is also what the empty state quotes back, and quoting a half-typed term
   * ("Nothing matches 'mono'") while the real query is still in flight would be
   * a message about a search nobody ran.
   */
  const [search, setSearch] = useState('')

  const { data, isLoading, isFetching, isRefetching } = useActivityFeed({
    limit: visibleCount,
    types: selectedTypes,
    q: search,
  })

  // `limit` is part of the query key, so growing the window starts a NEW query
  // and `data` is briefly undefined. Holding the last response keeps the feed on
  // screen while the wider page loads instead of collapsing back to a skeleton.
  const lastResponseRef = useRef<typeof data | null>(null)
  if (data) lastResponseRef.current = data

  const response = data ?? lastResponseRef.current
  const items = useMemo(() => response?.data ?? [], [response])
  const total = response?.total ?? 0
  const hasMore = items.length < total

  /*
   * The unseen/seen boundary.
   *
   * `lastSeenAt` is the watermark from the PREVIOUS visit — this hook writes the
   * new one in an effect, after this render has already read the old value, so
   * the divider is computed against what the reader had actually seen when they
   * arrived. See the hook for why this is localStorage and what that costs.
   *
   * Keyed to the profile id so two accounts on one device do not share a
   * watermark. `undefined` while the store is still loading the profile, which
   * the hook handles by reporting no watermark for that render.
   */
  const profileId = useSelector(({ auth }) => auth.profile?.id)
  const lastSeenAt = useActivityLastSeen(profileId)

  /*
   * Index of the FIRST already-seen row. The feed is server-ordered newest
   * first, so every row above this is new and every row below it is not — which
   * is what makes a single boundary sufficient rather than a per-row test.
   *
   * Two of the three outcomes deliberately render nothing:
   *   -1  no row is old enough → EVERY loaded row is new. A divider at the very
   *       top has nothing below it to label, and on a first-ever visit it would
   *       greet a new owner with a line telling them their whole history is
   *       unread.
   *    0  the newest row is already seen → nothing is new, so there is no
   *       boundary to draw.
   *   >0  a real boundary, with rows on both sides.
   *
   * `<=` and not `<`: a row written in the same millisecond the watermark was
   * stamped was on screen when it was stamped, so it has been seen.
   */
  const seenBoundaryIndex = useMemo(() => {
    if (lastSeenAt === null) return -1

    return items.findIndex(item => new Date(item.created_at).getTime() <= lastSeenAt)
  }, [items, lastSeenAt])

  const showSeenDivider = seenBoundaryIndex > 0

  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  /* Stuck-detection for the toolbar, same mechanism as the certificate page's
     own header flip. See the bar's own note for why it matters. */
  const toolbarSentinelRef = useRef<HTMLDivElement | null>(null)
  const isToolbarStuck = useScrolledPast(toolbarSentinelRef, 0)

  useEffect(() => {
    const sentinel = loadMoreRef.current
    if (!sentinel || !hasMore) return

    const observer = new IntersectionObserver(
      entries => {
        // Guard while a fetch is in flight: the sentinel stays mounted until the
        // wider page lands, so without this the observer queues several pages at
        // once. `isFetching` and not `isRefetching` alone, because growing the
        // window changes the key and so reads as a first load, not a refetch.
        if (entries[0]?.isIntersecting && !isFetching && !isRefetching) {
          setVisibleCount(count => count + FEED_PAGE_SIZE)
        }
      },
      { rootMargin: '200px' }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, isFetching, isRefetching])

  // Skeleton on the FIRST load only, never over content already on screen (§19).
  const isFirstLoad = (isLoading || isFetching) && !lastResponseRef.current

  return (
    <Stack
      sx={{
        background: theme.palette.background.paper,
        // Fill the viewport so the page background doesn't stop at the end of
        // the content and expose the dark backdrop beneath it. Same as every
        // other full page in this app.
        minHeight: '100vh',
        // NO `px` — the side gutter belongs to `PageContainer` below, which owns
        // it for every page.
        // NO `pt` — PageHeader owns the top gap (see its note). Worth knowing on
        // THIS page specifically: the toolbar below pins at `top: 0`, so the page
        // top gap is scrolled away by the time it matters and cannot be used to
        // reserve space for the pinned bar.
        pb: { xs: 4, md: 8 },
      }}
    >
      {/* Shared column outside, READING measure inside — a visual no-op that moves
          the gutter and the outer cap to one owner. See Profile for the full note.

          ⚠️ This page's feed is the SAME rows as the certificate's Activity
          SUB-VIEW, which now sits on the full 1120 column while this one keeps 640.
          That is a real inconsistency and it is called out in `PROSE_MAX_WIDTH`;
          it is not an oversight, and whichever way it resolves, both should move
          together. */}
      <PageContainer>
      <Box sx={{ width: '100%', maxWidth: PROSE_MAX_WIDTH, mx: 'auto' }}>
        {/*
          No back-link: a bottom-bar destination is not somewhere you came FROM,
          so one would be a false trail (same call the Notifications page made).
        */}
        <PageHeader
          title='Activity'
          description='Events across all of your certificates, newest first. Individual scans are listed on each certificate.'
          /*
            The count moved UP here from the toolbar, where it was the left half
            of a space-between row beside the Filter button. Two reasons, and the
            second is the load-bearing one:

            The toolbar is now icon-only, matching the certificates toolbar, so
            there is no text in it to keep the count company — a lone sentence
            beside a lone glyph is not a toolbar, it is a leftover.

            And this count is the ONLY thing on the page that says a filter did
            anything. Filtering to "Transfers" changes which rows are listed, but
            a reader scrolled into a long feed cannot see that the list got
            shorter. On the title row it is read on arrival and after every
            apply, rather than being scrolled past. It stays a live value, not
            copy, which is why it is `titleMeta` and not folded into the
            description above.
          */
          /*
            No event COUNT. It was promoted here from the toolbar to survive the
            icon-button change, but it is chrome: a number nobody asked for, printed
            above a list that already shows its own length, and it went stale-looking
            the moment a search narrowed the rows. The search field is the control that
            answers "did my filter do anything" far better than a total does.
          */

        />

        {/*
          STICKY, and glass — the one row of chrome on this page that is worth
          pinning.

          What earns the pin is the CONTROLS: on a feed that pages into the
          hundreds, the search field and the filter have to be reachable from
          wherever you have scrolled to, not only from the top. Search
          strengthens that case rather than merely joining it — a filter you
          cannot reach is an inconvenience, but a SEARCH you cannot reach while
          looking at page six of a feed is the whole feature.

          THE STUCK-ONLY TITLE IS GONE, and it was deliberate both times.

          It existed because a pinned bar holding one lone glyph, with the page
          title scrolled away above it, had nothing to say what it filtered — so
          the word "Activity" crossfaded in on stick to name the surface. That
          argument was sound and is now spent: the search field's placeholder
          reads "Search activity", permanently, in the same bar. The surface is
          named, so a second element naming it again is duplication.

          Keeping it was actively worse than dropping it, which is what settles
          it. The title rendered at zero opacity rather than unmounting, so that
          the Filter button would not slide sideways on every stick — meaning it
          reserved its width PERMANENTLY. Beside a field that wants to grow, that
          is a fixed dead column at the left of a 375px bar, unlabelled for most
          of the page's life.

          Same material as BottomNav and the certificate header — 0.82 fill
          behind blur + saturate. `saturate` is the half that does the work:
          blur alone greys out what passes under it and reads as frosted
          plastic. One glass in this app, not three approximations of one.
        */}
        <Box ref={toolbarSentinelRef} aria-hidden sx={{ height: '1px' }} />
        <Stack
          direction='row'
          alignItems='center'
          /*
           * THREE children became two, laid out the way the certificates
           * toolbar lays out the same pair: the field TAKES the space
           * (`flexGrow`) and the control KEEPS its size (`flexShrink: 0`).
           *
           * Not `space-between`, which is what this row used when its children
           * were a title and a button. With a growable field present it would
           * be the wrong instruction — it distributes slack between items, so
           * the field would sit at its content width with a gap beside it
           * rather than filling the bar.
           *
           * 8px gap (`spacing={1}` on this app's 8px MUI base — NOT the 4px
           * `@repo/ui` base, §29), matching the gap between the search field
           * and the controls beside it in the certificates HeaderBar. These are
           * one screen apart and should measure the same.
           */
          spacing={1}
          /*
            Background AND border colour set INLINE, not in `sx`.
            `#vera-shadow-root * { border-color: ... }` in index.css has ID
            specificity and silently discards an sx border colour, so a
            conditional hairline written there paints at rest regardless of the
            flag — which is exactly how it shipped wrong on the sub-view header.
            Inline always wins and is never emotion-class-cached across the
            state flip either (the VERA-476 divider bug).
          */
          style={{
            /* The shared token, in its inline form — see `utils/glass`. The
               `@supports` fallback cannot be expressed inline, so its half is
               spread into `sx` below; both are needed. */
            ...glassStyle(theme, isToolbarStuck),
            borderBottomColor: isToolbarStuck ? theme.palette.grey[200] : 'transparent',
          }}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            py: 1.5,
            mb: 1,
            /*
              Pulled up 8px. PageHeader ends its block with a 24px `mb` sized for
              PROSE following prose (§40's measured description -> content gap), and
              this toolbar then adds its own 12px of top padding on top of that — 36px
              between the caveat and a search field. A control does not need the air a
              paragraph does; it reads as detached from the header it belongs to.
              Negative margin rather than shrinking PageHeader's value, because that
              value is shared and correct for the pages whose next element IS prose.
            */
            mt: -1,
            /* Full-bleed while pinned, so rows do not slide past in the page's
               16px gutters beside the glass. */
            mx: { xs: -2, md: -4 },
            px: { xs: 2, md: 4 },
            /* Width/style only — the colour is inline above, so the space is
               always reserved and there is no 1px jump when it appears. */
            borderBottom: '1px solid transparent',
            transition: 'background-color .15s ease, border-color .15s ease',
            /* NEW here, not just a refactor: this bar previously had NO
               `@supports` fallback, so on a browser without backdrop-filter it
               pinned at 82% alpha and the feed stayed legible straight through
               it. Adopting the token is what surfaced the omission — which is
               the argument for the token. */
            ...glassUnsupportedSx(theme, isToolbarStuck),
          }}
        >
          {/*
            `minWidth: 0` is not decoration. A flex item's default `min-width:
            auto` refuses to shrink below its content, so without this the field
            would push the Filter button off the right edge of a 375px bar
            instead of giving way to it.
          */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <ToolbarSearchField
              placeholder='Search'
              /* Names the surface now that the stuck-only title is gone (see
                 the bar's note), and is the field's accessible name — a
                 placeholder is not one, since it vanishes on the first
                 keystroke. */
              ariaLabel='Search activity'  /* aria keeps the surface name; the VISIBLE placeholder matches the certificates toolbar */
              /*
                DEBOUNCED, unlike the certificates search, because this one is a
                REQUEST. Undebounced it would fire a query per keystroke, and
                since responses can land out of order, the list could settle on
                the results for a prefix of what is in the box.

                The shared 400ms, not a local number: it is what every other
                search in this app waits, and a feature that feels different
                from its neighbours for no reason is just an inconsistency.
              */
              debounceMs={searchDebounceDelay}
              onChange={value => {
                setSearch(value)
                /*
                  Back to the first page, exactly as applying a filter does and
                  for the same reason: the current window was counted against
                  the previous query, so carrying it over would ask the server
                  for rows 0..N of a result set the screen is not showing.
                */
                setVisibleCount(FEED_PAGE_SIZE)
                lastResponseRef.current = null
              }}
            />
          </Box>
          {/*
            THE shared `FilterControl` — the same component the certificates
            toolbar and the three certificate sub-views render, not another
            approximation of it.

            This was hand-rolled, and the comment that stood here claimed it was
            "copied down to the details rather than approximated". It was not,
            and the drift is the reason the hand-roll had to go: the label was a
            `Typography` pinned to `14px`, where the shared control deliberately
            INHERITS the `@repo/ui` Button's 16/500 (see `ToolbarLabel`); the
            glyph took lucide's default stroke 2 against the house
            `TOOLBAR_ICON_STROKE` of 1 (§21); and the count rendered as `Filter
            (2)` inside the label text rather than as the `CountPill` every other
            toolbar control in both apps uses — so on a phone, where the label is
            hidden, the count vanished with it and nothing on screen said a
            filter was active. Two hand-rolls asserting parity in prose is the
            §36 failure mode; the assertion is now structural.

            `fieldRow` is the one real difference and it is now a named band
            rather than an `sx` override: this Filter sits abreast of the 48px
            search field, so it takes the field-row height at every width instead
            of the button row's `{ xs: 44, md: 48 }`. See
            `toolbarControlFieldRowSx`.
          */}
          <FilterControl
            count={activeTypeCount}
            onClick={() => setFilterOpen(true)}
            subject='activity'
            fieldRow
          />
        </Stack>

        {isFirstLoad ? (
          <ActivityFeedSkeleton />
        ) : items.length === 0 ? (
          /* Both narrowings are passed so the copy can name the one that is
             actually responsible, rather than blaming the search for an empty
             type filter or vice versa. */
          <EmptyState query={search} typeFilterCount={activeTypeCount} />
        ) : (
          <Stack spacing={0}>
            {items.map((item, index) => {
              const { label } = getActivityTypeConfig(item.type)

              return (
                // `ListRow` is presentational, so the tap affordance is wrapped
                // around it here rather than added to the shared component —
                // the Scans and per-certificate Activity lists use the same row
                // without navigating, and giving it an onClick would invite the
                // two to drift.
                <React.Fragment key={item.id}>
                  {/* The divider is a SIBLING inside the same list, emitted just
                      before the first already-seen row, rather than a second
                      `.map()` over two slices. One list means the infinite-scroll
                      sentinel below still measures against the real last row, and
                      a boundary that moves (the watermark is per visit, and rows
                      arrive above it) cannot desynchronise two arrays. */}
                  {showSeenDivider && index === seenBoundaryIndex && <SeenDivider />}
                  <Box
                    role='button'
                    tabIndex={0}
                    aria-label={`${label} — ${item.certificate.name ?? 'certificate'}`}
                    onClick={() => navigate(`/certificates/${item.certificate.id}`)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        navigate(`/certificates/${item.certificate.id}`)
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.7 },
                      '&:focus-visible': {
                        outline: `2px solid ${theme.palette.text.primary}`,
                        outlineOffset: '-2px',
                      },
                    }}
                  >
                    <ListRow
                      /*
                        STATED, not inferred. `ListRow`'s fallback is
                        `&:last-of-type`, which only holds when rows are
                        siblings — and every row here is the only child of its
                        own tap-target `Box`, so that selector matched ALL of
                        them and the feed lost every separator while
                        `ActivityFeedSkeleton` kept them. The list changed shape
                        as it resolved, which is the one thing a footprint
                        skeleton exists to prevent (§19).
                      */
                      divider={
                        index !== items.length - 1 &&
                        // ...and NOT the row directly above the seen divider.
                        // That divider draws its own rule, so a hairline here
                        // put two horizontal lines a few pixels apart — the
                        // separator saying "next row" immediately restated by
                        // the one saying "everything below is older". One
                        // boundary should be drawn once, by whichever mark
                        // carries the MEANING, which is the labelled one.
                        !(showSeenDivider && index === seenBoundaryIndex - 1)
                      }
                      /*
                        ONE LINE EACH. The label is a finite phrase ("Transfer
                        initiated") but the SUBLABEL is a product title, which is
                        unbounded — and the row also carries a status chip and a
                        timestamp, so the text column is the only part that can
                        give. Left to wrap, "Transfer initiated" broke across two
                        lines and the product title across two more, turning a
                        two-line row into four and destroying the timestamp rail
                        that keeps the feed scannable.
                      */
                      truncate
                      leading={<CertificateThumb item={item} />}
                      // The EVENT is the headline and the certificate is the
                      // sublabel, not the other way round. A feed answers "what
                      // happened" first; which certificate it happened to is the
                      // qualifier, and it is also carried by the thumbnail.
                      label={label}
                      // Em-dash placeholder keeps every row two lines, so the
                      // timestamps stay on one rail (same rule as the Scans list).
                      sublabel={item.certificate.name || '—'}
                      sublabelMuted={!item.certificate.name}
                      // Renders nothing when the log carries no status, which is
                      // the common case — the component decides, so a row never
                      // shows an empty pill or an "Unknown".
                      /*
                        The CHIP, not the dot. This feed was the last `dot` call
                        site on a white ground, and VRG-511 already measured why
                        that fails: the dot's mid-tone renders at 1.21-1.46:1
                        against the card, i.e. the colour channel carries no
                        information at all, leaving the reader to decode status
                        from 14px secondary grey. The certificates card moved to a
                        tinted chip then; the certificate page's own Activity tab
                        has always used the default chip. This row was the odd one.

                        The density objection to pills in a list does not hold
                        here: the tint is a pale wash with a deep label, not the
                        saturated fill the original dot decision was made against.
                      */
                      meta={<CertificateStatusChip status={item.certificate_status} />}
                      /* `dateOnly` — "20 Aug", not "Aug 20, 02:26". The row's
                         text column is truncated to hold two lines against a
                         status chip and this timestamp, and the minute is the
                         cheapest thing in the row to give up: the full moment is
                         still in `timeTitle` directly below. See the option. */
                      time={formatActivityTime(item.created_at, { dateOnly: true })}
                      timeTitle={formatedDate({
                        date: item.created_at,
                        format: 'MMM do yyyy, h:mm a',
                      })}
                    />
                  </Box>
                </React.Fragment>
              )
            })}
          </Stack>
        )}

        {/* Infinite scroll: a sentinel below the list pulls the next page as it
            comes into view. Only rendered while the server holds more rows. */}
        {hasMore && !isFirstLoad && (
          /* Skeleton rows, not a spinner. A spinner is the thing §19 exists to
             replace — it says something is happening and nothing about what,
             while these say "more of exactly this is coming" and hold the space
             it will land in. Three rows rather than a screenful: they sit under
             content the reader is already looking at, so the job is to show the
             list continues, not to stand in for a page. */
          <Box ref={loadMoreRef}>
            <ActivityFeedSkeleton rows={3} />
          </Box>
        )}
      </Box>
      </PageContainer>

      {/*
        The per-certificate Activity filter, reused — same event-type GROUPS, so
        "Transfers" means the same set on both surfaces and a fix to that
        taxonomy lands in both at once (working-conventions §28).

        `showDateRange={false}`: this feed filters server-side, and the endpoint
        takes types but not yet a date range. A date control here could only be
        honoured over the loaded window, which is exactly the misreporting that
        server-side filtering was chosen to avoid — so the axis is absent until
        the API carries it rather than present and quietly partial.
      */}
      <ActivityFilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        showDateRange={false}
        initialValues={{ types: selectedTypes }}
        /* No `setFilterOpen(false)` — the drawer auto-applies, so this fires 400ms
           after any type is toggled and closing here would dismiss the sheet on the
           user's first tap. Dismissal is `onClose`'s job. */
        onApply={(values: ActivityFilterValues) => {
          setSelectedTypes(values.types)
          // Back to the first page: the old window was counted against the old
          // filter, so keeping it would request rows that no longer line up
          // with what is on screen.
          setVisibleCount(FEED_PAGE_SIZE)
          lastResponseRef.current = null
        }}
      />
    </Stack>
  )
}

export default ActivityPage
